// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { apiService } from "../../../services/api/apiService";
import { ApiServiceError } from "../../../services/api/apiError";
import type {
  ApiEnvelopeDto,
  DelegationResponseDto,
  DelegationSearchResponseDto,
} from "../dto/delegationDtos";
import type {
  CreateDelegationInputModel,
  DelegationModel,
  DelegationSearchResultModel,
  SetDelegationStatusInputModel,
} from "../models/delegationModels";
import {
  mapDelegationResponseDtoToModel,
  mapDelegationSearchResponseDtoToModel,
  toCreateDelegationRequestDto,
  toSetDelegationStatusRequestDto,
} from "../mappers/delegationMappers";
import { shouldUseDemoData } from "../../../services/api/demoData";

export interface DelegationsApiContext {
  token?: string;
  tenantId: string;
  allowTestModeRequests?: boolean;
}

function unwrapApiEnvelope<T>(envelope: ApiEnvelopeDto<T>): T {
  if (envelope.error !== undefined) {
    throw new Error(envelope.error.message);
  }

  if (envelope.data === undefined) {
    throw new Error("API response was missing the expected data payload.");
  }

  return envelope.data;
}

function tenantHeaders(tenantId: string): HeadersInit {
  return { "X-Tenant-Id": tenantId };
}

function tokenOption(
  context: DelegationsApiContext,
): { token: string } | Record<string, never> {
  return context.token !== undefined ? { token: context.token } : {};
}

function buildInitialDemoDelegations(): DelegationModel[] {
  const seededAtUtc = "2026-02-12T09:00:00Z";

  const base = (
    overrides: Partial<DelegationModel> &
      Pick<
        DelegationModel,
        "delegationId" | "workTaskId" | "delegateType" | "delegateId"
      >,
  ): DelegationModel => ({
    tenantId: "tenant-current",
    status: "offered",
    offeredAtUtc: seededAtUtc,
    createdAtUtc: seededAtUtc,
    ...overrides,
  });

  return [
    base({
      delegationId: "demo-delegation-1",
      workTaskId: "demo-task-1",
      delegateType: "assistant",
      delegateId: "demo-assistant-1",
      delegatorActorId: "demo-agent-1",
      status: "accepted",
      respondedAtUtc: "2026-02-12T10:00:00Z",
    }),
    base({
      delegationId: "demo-delegation-2",
      workTaskId: "demo-task-2",
      delegateType: "vendor",
      delegateId: "demo-vendor-1",
    }),
  ];
}

let demoDelegations: DelegationModel[] = buildInitialDemoDelegations();
let demoDelegationSequence = demoDelegations.length;

export function __resetDemoDelegationsForTests(): void {
  demoDelegations = buildInitialDemoDelegations();
  demoDelegationSequence = demoDelegations.length;
}

function searchDemoDelegations(
  options?: SearchDelegationsOptions,
): DelegationSearchResultModel {
  const pageNumber =
    options?.pageNumber && options.pageNumber >= 1 ? options.pageNumber : 1;
  const pageSize =
    options?.pageSize && options.pageSize >= 1 ? options.pageSize : 25;

  let matches = [...demoDelegations];

  if (options?.workTaskId) {
    matches = matches.filter(
      (delegation) => delegation.workTaskId === options.workTaskId,
    );
  }

  if (options?.status) {
    matches = matches.filter(
      (delegation) => delegation.status === options.status,
    );
  }

  if (options?.delegateId) {
    matches = matches.filter(
      (delegation) => delegation.delegateId === options.delegateId,
    );
  }

  if (options?.delegatorActorId) {
    matches = matches.filter(
      (delegation) => delegation.delegatorActorId === options.delegatorActorId,
    );
  }

  matches.sort((a, b) => b.createdAtUtc.localeCompare(a.createdAtUtc));

  const start = (pageNumber - 1) * pageSize;
  const page = matches.slice(start, start + pageSize);

  return {
    delegations: page,
    pageNumber,
    pageSize,
    totalCount: matches.length,
  };
}

export interface SearchDelegationsOptions {
  workTaskId?: string | undefined;
  status?: string | undefined;
  delegateId?: string | undefined;
  delegatorActorId?: string | undefined;
  pageNumber?: number;
  pageSize?: number;
}

export async function searchDelegations(
  context: DelegationsApiContext,
  options?: SearchDelegationsOptions,
): Promise<DelegationSearchResultModel> {
  if (shouldUseDemoData(context)) {
    return searchDemoDelegations(options);
  }

  const response = await apiService.requestJson<
    ApiEnvelopeDto<DelegationSearchResponseDto>
  >({
    path: "/api/work/delegations",
    method: "GET",
    ...tokenOption(context),
    headers: tenantHeaders(context.tenantId),
    query: {
      workTaskId: options?.workTaskId,
      status: options?.status,
      delegateId: options?.delegateId,
      delegatorActorId: options?.delegatorActorId,
      pageNumber: options?.pageNumber,
      pageSize: options?.pageSize,
    },
  });

  return response
    .map(unwrapApiEnvelope)
    .map(mapDelegationSearchResponseDtoToModel)
    .unwrap();
}

export async function getDelegation(
  context: DelegationsApiContext,
  delegationId: string,
): Promise<DelegationModel | null> {
  if (shouldUseDemoData(context)) {
    return (
      demoDelegations.find((delegation) => delegation.delegationId === delegationId) ?? null
    );
  }

  try {
    const response = await apiService.requestJson<ApiEnvelopeDto<DelegationResponseDto>>({
      path: `/api/work/delegations/${encodeURIComponent(delegationId)}`,
      method: "GET",
      ...tokenOption(context),
      headers: tenantHeaders(context.tenantId),
    });

    return response
      .map(unwrapApiEnvelope)
      .map(mapDelegationResponseDtoToModel)
      .unwrap();
  } catch (error) {
    if (error instanceof ApiServiceError && error.status === 404) {
      return null;
    }

    throw error;
  }
}

export async function createDelegation(
  context: DelegationsApiContext,
  input: CreateDelegationInputModel,
): Promise<DelegationModel> {
  if (shouldUseDemoData(context)) {
    demoDelegationSequence += 1;
    const nowIso = new Date().toISOString();
    const created: DelegationModel = {
      delegationId: `demo-delegation-${demoDelegationSequence}`,
      tenantId: context.tenantId,
      workTaskId: input.workTaskId,
      delegateType: input.delegateType,
      delegateId: input.delegateId,
      status: "offered",
      offeredAtUtc: nowIso,
      createdAtUtc: nowIso,
      ...(input.notes !== undefined ? { notes: input.notes } : {}),
    };
    demoDelegations = [...demoDelegations, created];
    return created;
  }

  const response = await apiService.requestJson<
    ApiEnvelopeDto<DelegationResponseDto>,
    ReturnType<typeof toCreateDelegationRequestDto>
  >({
    path: "/api/work/delegations",
    method: "POST",
    ...tokenOption(context),
    headers: tenantHeaders(context.tenantId),
    body: toCreateDelegationRequestDto(context.tenantId, input),
  });

  return response
    .map(unwrapApiEnvelope)
    .map(mapDelegationResponseDtoToModel)
    .unwrap();
}

export async function setDelegationStatus(
  context: DelegationsApiContext,
  input: SetDelegationStatusInputModel,
): Promise<DelegationModel> {
  if (shouldUseDemoData(context)) {
    const existing = demoDelegations.find(
      (delegation) => delegation.delegationId === input.delegationId,
    );
    if (!existing) {
      throw new Error("The requested delegation was not found.");
    }

    const nowIso = new Date().toISOString();
    const updated: DelegationModel = {
      ...existing,
      status: input.status,
      updatedAtUtc: nowIso,
      ...((input.status === "accepted" || input.status === "declined") &&
      existing.respondedAtUtc === undefined
        ? { respondedAtUtc: nowIso }
        : {}),
      ...(input.status === "completed" && existing.completedAtUtc === undefined
        ? { completedAtUtc: nowIso }
        : {}),
    };
    demoDelegations = demoDelegations.map((delegation) =>
      delegation.delegationId === updated.delegationId ? updated : delegation,
    );
    return updated;
  }

  const response = await apiService.requestJson<
    ApiEnvelopeDto<DelegationResponseDto>,
    ReturnType<typeof toSetDelegationStatusRequestDto>
  >({
    path: `/api/work/delegations/${encodeURIComponent(input.delegationId)}/status`,
    method: "PATCH",
    ...tokenOption(context),
    headers: tenantHeaders(context.tenantId),
    body: toSetDelegationStatusRequestDto(context.tenantId, input),
  });

  return response
    .map(unwrapApiEnvelope)
    .map(mapDelegationResponseDtoToModel)
    .unwrap();
}
